/**
 * Landing content for the 4 service and 3 commodity pages.
 *
 * ─── CONTENT GOVERNANCE ─────────────────────────────────────────────
 * Nothing here is an invented company claim.
 *
 *   • "Overview"     — neutral positional statement, deliberately short.
 *   • "Capabilities" — the *industry-standard* set of trading-house
 *                      capabilities. These are structural, not proof.
 *   • "Industries"   — the *industries that buy this commodity / use
 *                      this service*, based on public commercial usage.
 *   • "Process"      — the universal physical commodity trade cycle
 *                      (RFQ → contract → payment → logistics → docs
 *                      → discharge). Same for every serious trading
 *                      counterparty on the planet.
 *   • "Why BNM"      — expressed only as **positioning attributes** of
 *                      an Italian S.r.l. operating in the EU. No metrics,
 *                      no history, no volumes, no certifications, no
 *                      customer names. Any company-specific proof will
 *                      be added later from the official Source of Truth.
 * ────────────────────────────────────────────────────────────────────
 */

import type { RouteKey } from "@/lib/routes";

export interface Bullet {
  title: string;
  body: string;
}

export interface ProcessStep {
  title: string;
  body: string;
}

export interface LandingContent {
  /** Short paragraph shown next to the "Overview" eyebrow. */
  overview: string;
  capabilities: Bullet[];
  industries: string[];
  process: ProcessStep[];
  whyBnm: Bullet[];
}

export interface LandingBundle {
  en: LandingContent;
  it: LandingContent;
}

// ─── SHARED POSITIONAL ATTRIBUTES (used by all pages) ─────────────
const WHY_BNM_EN: Bullet[] = [
  {
    title: "Italian S.r.l. counterparty",
    body: "Trading and contracting are executed by a company registered under Italian corporate law, with public records verifiable through the Italian Registro Imprese.",
  },
  {
    title: "EU regulatory perimeter",
    body: "Operations are conducted within the European Union regulatory framework — including sanctions, food safety, dual-use and customs regimes.",
  },
  {
    title: "Mediterranean position",
    body: "Italy sits at the intersection of Mediterranean, Northern-European and MENA trade corridors, with direct access to major ports.",
  },
  {
    title: "Single-counterparty across three verticals",
    body: "Grains, food products and petrochemicals coordinated through a single commercial and documentary interface.",
  },
  {
    title: "Compliance-led posture",
    body: "Published KYC / counterparty verification workflow and an anti-fraud notice — both linked directly from the site header.",
  },
];

const WHY_BNM_IT: Bullet[] = [
  {
    title: "Controparte S.r.l. italiana",
    body: "L'attività di trading è eseguita da una società registrata secondo il diritto societario italiano, con dati pubblici verificabili tramite il Registro Imprese.",
  },
  {
    title: "Perimetro regolatorio UE",
    body: "Le operazioni si svolgono all'interno del quadro regolatorio dell'Unione Europea — inclusi regimi sanzionatori, sicurezza alimentare, dual-use e dogana.",
  },
  {
    title: "Posizione mediterranea",
    body: "L'Italia si trova all'incrocio dei corridoi mediterranei, nordeuropei e MENA, con accesso diretto ai principali porti.",
  },
  {
    title: "Unica controparte su tre verticali",
    body: "Cereali, prodotti alimentari e petrolchimici gestiti attraverso un'unica interfaccia commerciale e documentale.",
  },
  {
    title: "Postura orientata alla compliance",
    body: "Flusso KYC / verifica controparte pubblicato e avviso anti-frode, entrambi collegati direttamente dal header del sito.",
  },
];

// ─── SHARED STANDARD TRADE PROCESS ────────────────────────────────
const PROCESS_EN: ProcessStep[] = [
  { title: "Inquiry",                 body: "The buyer or seller submits an RFQ with product specification, volume, delivery window and target Incoterms." },
  { title: "Offer & counter-offer",   body: "Commercial terms are exchanged — price basis, quality specs, laytime, payment and demurrage clauses." },
  { title: "Contract execution",      body: "Physical contract is executed under standard international terms (GAFTA / FOSFA / INCOTERMS / relevant industry contract)." },
  { title: "Payment structure",       body: "Payment instrument is issued — Documentary Letter of Credit, CAD, or agreed alternative — with independent bank verification." },
  { title: "Logistics & inspection",  body: "Loading is coordinated with the shipping line and independent surveyors (SGS, Bureau Veritas or equivalent) for quality and quantity." },
  { title: "Documentation",           body: "Full commercial and shipping document set is issued: bill of lading, certificate of origin, quality and phytosanitary certificates as required." },
  { title: "Discharge & settlement",  body: "Cargo is discharged at the destination port; final documents and payment are reconciled and archived per contract." },
];

const PROCESS_IT: ProcessStep[] = [
  { title: "Richiesta / RFQ",         body: "Il buyer o il seller invia una RFQ con specifica prodotto, volume, finestra di consegna e Incoterms target." },
  { title: "Offerta e controproposta",body: "Si scambiano i termini commerciali — base prezzo, specifiche qualitative, laytime, condizioni di pagamento e demurrage." },
  { title: "Esecuzione del contratto",body: "Il contratto fisico è firmato secondo termini internazionali standard (GAFTA / FOSFA / INCOTERMS / contratto di settore)." },
  { title: "Struttura di pagamento",  body: "Emissione dello strumento di pagamento — Lettera di Credito documentaria, CAD o alternativa concordata — con verifica bancaria indipendente." },
  { title: "Logistica e ispezione",   body: "Il carico è coordinato con la compagnia di navigazione e con surveyor indipendenti (SGS, Bureau Veritas o equivalente) per qualità e quantità." },
  { title: "Documentazione",          body: "Emissione del set completo di documenti commerciali e di spedizione: polizza di carico, certificato di origine, certificati di qualità e fitosanitari." },
  { title: "Discharge e settlement",  body: "Il carico è discaricato al porto di destinazione; documenti finali e pagamento sono riconciliati e archiviati secondo contratto." },
];

// ═══════════════════════════════════════════════════════════════════
// SERVICES
// ═══════════════════════════════════════════════════════════════════

const S_TRADE_EN: LandingContent = {
  overview:
    "Cross-border trade of physical commodities executed as principal or as intermediary, under standard international terms.",
  capabilities: [
    { title: "Contract structuring",       body: "Physical contracts under GAFTA, FOSFA, NAEGA and Incoterms 2020, adapted to each cargo and jurisdiction." },
    { title: "Counterparty due diligence", body: "KYC / KYS screening on both buyer and seller before commercial commitment." },
    { title: "Payment engineering",        body: "Letters of credit, CAD, avalised drafts and bank-to-bank structures with independent verification." },
    { title: "Documentary handling",       body: "Full commercial and shipping document set, aligned with LC terms and destination-country requirements." },
    { title: "Logistics coordination",     body: "Vessel nomination, load-port supervision, marine insurance, discharge coordination." },
    { title: "Regulatory & customs",       body: "EU / third-country customs positioning, dual-use screening, sanctions and export-control review." },
  ],
  industries: [
    "Institutional buyers", "State-owned importers", "Industrial end-users",
    "Cooperatives", "Distribution groups", "Traders and brokers",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const S_TRADE_IT: LandingContent = {
  overview:
    "Compravendita transfrontaliera di commodities fisiche eseguita come principale o come intermediario, secondo termini internazionali standard.",
  capabilities: [
    { title: "Strutturazione contrattuale", body: "Contratti fisici GAFTA, FOSFA, NAEGA e Incoterms 2020, adattati a ogni cargo e giurisdizione." },
    { title: "Due diligence controparte",   body: "Screening KYC / KYS su buyer e seller prima dell'impegno commerciale." },
    { title: "Ingegneria dei pagamenti",    body: "Lettere di credito, CAD, tratte avallate e strutture bank-to-bank con verifica indipendente." },
    { title: "Gestione documentale",        body: "Set completo di documenti commerciali e di spedizione, coerenti con LC e paese di destinazione." },
    { title: "Coordinamento logistico",     body: "Nomina della nave, supervisione al porto di caricamento, assicurazione marittima, coordinamento allo sbarco." },
    { title: "Regolatorio e doganale",      body: "Posizionamento doganale UE / paesi terzi, screening dual-use, sanzioni ed export control." },
  ],
  industries: [
    "Buyer istituzionali", "Importatori statali", "Utenti industriali",
    "Cooperative", "Gruppi di distribuzione", "Trader e broker",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

const S_SOURCING_EN: LandingContent = {
  overview:
    "Origination and qualification of suppliers across multiple geographies, matched to the buyer's technical specification and commercial constraints.",
  capabilities: [
    { title: "Origin qualification",   body: "Structured supplier vetting on legal, operational, financial and reputational criteria." },
    { title: "Specification alignment",body: "Technical spec, sampling and pre-shipment analysis coordinated with the end-buyer's QC." },
    { title: "Volume aggregation",     body: "Consolidation of tonnage from qualified origins to meet institutional volumes." },
    { title: "Backup origin planning", body: "Alternative origins pre-qualified to mitigate weather, geopolitical or logistical disruption." },
    { title: "Reference pricing",      body: "Formula pricing linked to public benchmarks (CBOT, MATIF, Platts) with agreed hedging windows." },
  ],
  industries: [
    "Milling groups", "Feed producers", "Food ingredient manufacturers",
    "Petrochemical converters", "Trading houses", "Public procurement",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const S_SOURCING_IT: LandingContent = {
  overview:
    "Selezione e qualifica di fornitori in molteplici aree geografiche, allineati alle specifiche tecniche e ai vincoli commerciali del buyer.",
  capabilities: [
    { title: "Qualifica dell'origine",    body: "Vetting strutturato dei fornitori su criteri legali, operativi, finanziari e reputazionali." },
    { title: "Allineamento specifiche",   body: "Specifica tecnica, campionatura e analisi pre-shipment coordinate con il QC del buyer finale." },
    { title: "Aggregazione volumi",       body: "Consolidamento di tonnellaggio da origini qualificate per volumi istituzionali." },
    { title: "Origini di backup",         body: "Origini alternative pre-qualificate per mitigare rischi meteo, geopolitici o logistici." },
    { title: "Pricing di riferimento",    body: "Prezzi a formula legati a benchmark pubblici (CBOT, MATIF, Platts) con finestre di hedging concordate." },
  ],
  industries: [
    "Gruppi molitori", "Produttori di mangimi", "Produttori di ingredienti alimentari",
    "Convertitori petrolchimici", "Trading house", "Approvvigionamento pubblico",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

const S_SUPPLY_EN: LandingContent = {
  overview:
    "Structured, recurring physical supply of grains, food products and petrochemicals to institutional buyers under multi-shipment frameworks.",
  capabilities: [
    { title: "Framework contracts",        body: "Multi-shipment master agreements with agreed release mechanics, pricing formulas and quality bands." },
    { title: "Shipping scheduling",        body: "Vessel and stowage planning across successive lay-can windows to keep the buyer's plant supplied." },
    { title: "Quality bands & tolerances", body: "Contractual quality corridor with allowances, deductions and rejection thresholds pre-agreed." },
    { title: "Documentary continuity",     body: "Standardised document templates across shipments to simplify buyer-side reconciliation." },
    { title: "Risk sharing",               body: "Freight, FX and price-risk allocation defined contractually rather than negotiated per shipment." },
  ],
  industries: [
    "Flour and semolina mills", "Feed compounders", "Refineries and petchem plants",
    "Industrial food producers", "National procurement bodies",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const S_SUPPLY_IT: LandingContent = {
  overview:
    "Fornitura fisica strutturata e ricorrente di cereali, prodotti alimentari e petrolchimici a buyer istituzionali, sotto framework pluri-spedizione.",
  capabilities: [
    { title: "Contratti framework",        body: "Master multi-shipment con meccanismi di rilascio, formule di prezzo e bande qualità concordate." },
    { title: "Pianificazione spedizioni",  body: "Nomina navi e stivaggio su finestre lay-can successive per mantenere l'impianto del buyer alimentato." },
    { title: "Bande qualità e tolleranze", body: "Corridoio qualità contrattuale con premi, sconti e soglie di rigetto pre-definite." },
    { title: "Continuità documentale",     body: "Template documentali standardizzati tra spedizioni per semplificare la riconciliazione lato buyer." },
    { title: "Ripartizione dei rischi",    body: "Rischio nolo, FX e prezzo allocato contrattualmente, non rinegoziato per spedizione." },
  ],
  industries: [
    "Mulini di grano tenero e semola", "Mangimifici", "Raffinerie e impianti petrolchimici",
    "Produttori industriali alimentari", "Enti nazionali di approvvigionamento",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

const S_CONSULT_EN: LandingContent = {
  overview:
    "Advisory on international commercial structures, counterparty verification and trade execution — for buyers, sellers and institutional actors who need a compliant Italian interface.",
  capabilities: [
    { title: "Structure design",           body: "Selection of the appropriate contractual, payment and Incoterms basis for a given commodity and geography." },
    { title: "Counterparty verification",  body: "Independent verification of the buyer or seller through official Italian and international sources." },
    { title: "Dispute prevention",         body: "Contract clauses, escrow structures and arbitration seats designed to minimise litigation exposure." },
    { title: "Documentary review",         body: "Pre-shipment review of the LC, contract, quality certificates and shipping documents for internal consistency." },
    { title: "Sanctions & compliance",     body: "Screening against EU / UN / UK / US sanctions regimes and export-control lists." },
  ],
  industries: [
    "Buyers entering EU markets", "Sellers exporting through Italy",
    "Public entities", "Family-owned commodity groups", "Emerging-market corporates",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const S_CONSULT_IT: LandingContent = {
  overview:
    "Consulenza su strutture commerciali internazionali, verifica delle controparti ed esecuzione delle transazioni — per buyer, seller ed enti istituzionali che necessitano di un'interfaccia italiana compliant.",
  capabilities: [
    { title: "Progettazione della struttura",body: "Selezione della base contrattuale, di pagamento e Incoterms adeguata alla commodity e alla geografia." },
    { title: "Verifica della controparte",   body: "Verifica indipendente del buyer o del seller attraverso fonti ufficiali italiane e internazionali." },
    { title: "Prevenzione dei contenziosi",  body: "Clausole contrattuali, strutture di escrow e sedi arbitrali progettate per minimizzare l'esposizione." },
    { title: "Review documentale",           body: "Verifica pre-spedizione di LC, contratto, certificati di qualità e documenti di spedizione." },
    { title: "Sanzioni e compliance",        body: "Screening rispetto ai regimi sanzionatori UE / ONU / UK / USA e agli elenchi di export control." },
  ],
  industries: [
    "Buyer in ingresso sui mercati UE", "Seller in esportazione via Italia",
    "Enti pubblici", "Gruppi commodity a controllo familiare", "Corporate emergenti",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

// ═══════════════════════════════════════════════════════════════════
// COMMODITIES
// ═══════════════════════════════════════════════════════════════════

const C_GRAINS_EN: LandingContent = {
  overview:
    "Physical trade and supply of cereals and derived milling products. Standard bulk contracts, GAFTA / FOSFA frameworks, and pre-shipment quality control.",
  capabilities: [
    { title: "Product coverage",       body: "Milling wheat, feed wheat, durum wheat, corn, barley, soybeans and soybean meal, sorghum, sunflower and rapeseed derivatives." },
    { title: "Standard specifications",body: "Protein bands, hectolitre weight, moisture, foreign matter, damaged kernels, mycotoxin thresholds per contract." },
    { title: "Origins",                body: "Black Sea, Latin America, EU-27, North America and Australia — subject to seasonality and origin restrictions." },
    { title: "Contract frameworks",    body: "GAFTA 49 / 78 / 100 series, FOSFA equivalents, NAEGA for North-American origins." },
    { title: "Quality assurance",      body: "Pre-shipment sampling and analysis by independent surveyors; certificates of quality issued at load port." },
  ],
  industries: [
    "Flour mills", "Semolina and pasta industry", "Feed compounders",
    "Malting industry", "Biofuel producers", "Public grain agencies",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const C_GRAINS_IT: LandingContent = {
  overview:
    "Commercio e fornitura fisica di cereali e prodotti di macinazione. Contratti bulk standard, framework GAFTA / FOSFA e controllo qualità pre-spedizione.",
  capabilities: [
    { title: "Copertura prodotto",        body: "Grano tenero da macinazione, grano da mangime, grano duro, mais, orzo, soia e farina di soia, sorgo, girasole e derivati di colza." },
    { title: "Specifiche standard",       body: "Bande di proteine, peso ettolitrico, umidità, corpi estranei, chicchi danneggiati e soglie micotossine da contratto." },
    { title: "Origini",                   body: "Mar Nero, America Latina, UE-27, Nord America e Australia — soggetto a stagionalità e restrizioni all'esportazione." },
    { title: "Framework contrattuali",    body: "Serie GAFTA 49 / 78 / 100, equivalenti FOSFA, NAEGA per origini nordamericane." },
    { title: "Assicurazione qualità",     body: "Campionatura e analisi pre-spedizione da surveyor indipendenti; certificati di qualità al porto di caricamento." },
  ],
  industries: [
    "Mulini di grano tenero", "Industria semola e pasta", "Mangimifici",
    "Industria del malto", "Produttori di biofuel", "Agenzie pubbliche cerealicole",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

const C_FOOD_EN: LandingContent = {
  overview:
    "Selected food commodities and industrial food ingredients supplied to processors, distributors and institutional buyers.",
  capabilities: [
    { title: "Product coverage",       body: "Vegetable oils (olive, sunflower, rapeseed, soya), tomato derivatives, pulses, sugar, coffee, and ingredient categories on request." },
    { title: "Food-safety framework",  body: "EU Regulation 178/2002 traceability, GFSI-recognised schemes (BRCGS / IFS / FSSC 22000) where applicable at origin." },
    { title: "Labelling & compliance", body: "Country-of-origin, allergen and nutritional labelling aligned with the destination-market regime." },
    { title: "Packaging formats",      body: "Bulk, IBC, drum, big-bag and retail-ready formats depending on the industrial user." },
    { title: "Cold-chain & FEFO",      body: "Where required — temperature-controlled logistics and FEFO stock rotation." },
  ],
  industries: [
    "Food processors", "HORECA distributors", "Private-label importers",
    "Ingredient manufacturers", "Retail chains through wholesalers",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const C_FOOD_IT: LandingContent = {
  overview:
    "Commodities alimentari selezionate e ingredienti industriali forniti a trasformatori, distributori e buyer istituzionali.",
  capabilities: [
    { title: "Copertura prodotto",           body: "Oli vegetali (oliva, girasole, colza, soia), derivati del pomodoro, legumi, zucchero, caffè e categorie di ingredienti su richiesta." },
    { title: "Framework di sicurezza alimentare", body: "Tracciabilità Reg. UE 178/2002, schemi GFSI (BRCGS / IFS / FSSC 22000) dove applicabile all'origine." },
    { title: "Etichettatura e compliance",   body: "Origine, allergeni e nutrizionali allineati al regime del mercato di destinazione." },
    { title: "Formati di packaging",         body: "Bulk, IBC, fusto, big-bag e formati retail-ready in base all'utilizzatore industriale." },
    { title: "Catena del freddo & FEFO",     body: "Dove richiesto — logistica a temperatura controllata e rotazione FEFO." },
  ],
  industries: [
    "Trasformatori alimentari", "Distributori HORECA", "Importatori private-label",
    "Produttori di ingredienti", "Catene retail via wholesaler",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

const C_PETRO_EN: LandingContent = {
  overview:
    "Base petrochemicals and refined derivatives for industrial users — sourced from qualified producers and traded under bulk contracts.",
  capabilities: [
    { title: "Product coverage",       body: "Base olefins and aromatics (ethylene, propylene, benzene), polymers (PE, PP, PVC, PET), MEG, urea, methanol and selected specialty derivatives." },
    { title: "Grades & specifications",body: "Product grades to standard industry specs (ASTM, ISO) with certificate of analysis at load port." },
    { title: "Logistics formats",      body: "Bulk vessel, iso-tank, IBC, 25-kg bag and big-bag depending on product and consignee capability." },
    { title: "Hazard classification",  body: "ADR / IMDG / IATA classification handled by qualified freight forwarders." },
    { title: "Dual-use screening",     body: "Systematic screening against EU dual-use lists and end-use certificates where relevant." },
  ],
  industries: [
    "Plastic and packaging converters", "Fertiliser and agrochemicals",
    "Textile and fibre producers", "Automotive and appliance industries",
    "Construction and infrastructure", "Industrial chemicals",
  ],
  process: PROCESS_EN,
  whyBnm: WHY_BNM_EN,
};

const C_PETRO_IT: LandingContent = {
  overview:
    "Petrolchimici di base e derivati raffinati per utenti industriali — approvvigionati da produttori qualificati e trattati con contratti bulk.",
  capabilities: [
    { title: "Copertura prodotto",       body: "Olefine e aromatici di base (etilene, propilene, benzene), polimeri (PE, PP, PVC, PET), MEG, urea, metanolo e derivati specialistici selezionati." },
    { title: "Gradi e specifiche",       body: "Gradi prodotto secondo specifiche industriali standard (ASTM, ISO) con certificato di analisi al porto di caricamento." },
    { title: "Formati logistici",        body: "Nave bulk, iso-tank, IBC, sacco 25 kg e big-bag in funzione del prodotto e del consegnatario." },
    { title: "Classificazione pericoli", body: "Classificazione ADR / IMDG / IATA gestita da spedizionieri qualificati." },
    { title: "Screening dual-use",       body: "Screening sistematico rispetto alle liste dual-use UE e certificati end-use quando applicabile." },
  ],
  industries: [
    "Convertitori plastica e packaging", "Fertilizzanti e agrochimici",
    "Tessile e fibre", "Automotive ed elettrodomestici",
    "Costruzioni e infrastrutture", "Chimica industriale",
  ],
  process: PROCESS_IT,
  whyBnm: WHY_BNM_IT,
};

// ─── REGISTRY ──────────────────────────────────────────────────────
export const LANDING_CONTENT: Partial<Record<RouteKey, LandingBundle>> = {
  "services.trade":                { en: S_TRADE_EN,    it: S_TRADE_IT },
  "services.sourcing":             { en: S_SOURCING_EN, it: S_SOURCING_IT },
  "services.supply":               { en: S_SUPPLY_EN,   it: S_SUPPLY_IT },
  "services.consulting":           { en: S_CONSULT_EN,  it: S_CONSULT_IT },
  "commodities.grains":            { en: C_GRAINS_EN,   it: C_GRAINS_IT },
  "commodities.food":              { en: C_FOOD_EN,     it: C_FOOD_IT },
  "commodities.petrochemical":     { en: C_PETRO_EN,    it: C_PETRO_IT },
};

// ─── LABELS FOR SECTION HEADERS ────────────────────────────────────
export const LANDING_LABELS = {
  en: {
    overviewEyebrow:    "01 — Overview",
    capabilitiesEyebrow:"02 — Capabilities",
    industriesEyebrow:  "03 — Industries served",
    processEyebrow:     "04 — Trade process",
    whyEyebrow:         "05 — Positioning",
    ctaEyebrow:         "06 — Commercial contact",
    overviewLabel:      "Overview",
    capabilitiesLabel:  "Capabilities",
    industriesLabel:    "Industries served",
    processLabel:       "Standard trade process",
    whyLabel:           "Why an Italian counterparty",
    processFootnote:    "Universal physical-commodity trade cycle. Individual clauses adapt to product, jurisdiction and Incoterms basis.",
    industriesFootnote: "Non-exhaustive list of typical buyer categories.",
    whyFootnote:        "Positioning attributes only. Specific metrics and references are not published on this page.",
  },
  it: {
    overviewEyebrow:    "01 — Panoramica",
    capabilitiesEyebrow:"02 — Capacità",
    industriesEyebrow:  "03 — Settori serviti",
    processEyebrow:     "04 — Processo commerciale",
    whyEyebrow:         "05 — Posizionamento",
    ctaEyebrow:         "06 — Contatto commerciale",
    overviewLabel:      "Panoramica",
    capabilitiesLabel:  "Capacità",
    industriesLabel:    "Settori serviti",
    processLabel:       "Processo commerciale standard",
    whyLabel:           "Perché una controparte italiana",
    processFootnote:    "Ciclo universale del commercio fisico di commodities. Le clausole si adattano a prodotto, giurisdizione e Incoterms.",
    industriesFootnote: "Elenco non esaustivo delle categorie tipiche di buyer.",
    whyFootnote:        "Solo attributi di posizionamento. Metriche e riferimenti specifici non sono pubblicati in questa pagina.",
  },
} as const;
